import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { formatDateTime } from "./helpers";
import { tNegocio } from "./shared";
import { PaymentStatusChip } from "./PublicidadPaymentStatus";

const t = (key) => tNegocio(`pages.publicidad.plans.review.${key}`);

const useStyles = makeStyles((theme) => ({
  wrap: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: 12,
    marginBottom: 18,
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 },
  title: { fontWeight: 800, fontSize: 15 },
  sub: { fontSize: 12, color: theme.palette.text.secondary },
  tableWrap: { overflowX: "auto" },
  receipt: { maxWidth: "100%", borderRadius: 8, display: "block", margin: "0 auto" },
}));

/** Solo superadmin: comprobantes de Publicidad IA por aprobar y órdenes pagadas que no se activaron. */
export default function PublicidadPaymentsReview({ onChanged }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/negocio/publicidad/admin/orders");
      setRows(Array.isArray(data?.records) ? data.records : []);
    } catch (error) {
      toastError(error);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => () => { if (receipt?.url) URL.revokeObjectURL(receipt.url); }, [receipt]);

  const run = async (work) => {
    setBusy(true);
    try { await work(); await load(); onChanged?.(); } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const showReceipt = (row) => run(async () => {
    const { data } = await api.get(`/negocio/publicidad/admin/orders/${encodeURIComponent(row.orderId)}/receipt`, { responseType: "blob" });
    setReceipt({ row, url: URL.createObjectURL(data) });
  });

  const approve = (row) => run(async () => {
    const { data } = await api.post(`/negocio/publicidad/admin/orders/${encodeURIComponent(row.orderId)}/approve`);
    toast[data.partnerError ? "warning" : "success"](data.partnerError ? t("approvedPending") : t("approved"));
    setReceipt(null);
  });

  const reject = () => run(async () => {
    await api.post(`/negocio/publicidad/admin/orders/${encodeURIComponent(rejecting.orderId)}/reject`, { message: reason.trim() });
    toast.success(t("rejected"));
    setRejecting(null);
    setReceipt(null);
  });

  const toReview = rows.filter((r) => r.paymentStatus === "review").length;

  return (
    <div className={classes.wrap}>
      <div className={classes.head}>
        <div>
          <div className={classes.title}>{t("title")}{toReview ? ` (${toReview})` : ""}</div>
          <div className={classes.sub}>{t("subtitle")}</div>
        </div>
        <Button size="small" onClick={load} disabled={busy}>{t("refresh")}</Button>
      </div>
      <div className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("colCompany")}</TableCell>
              <TableCell>{t("colOrder")}</TableCell>
              <TableCell>{t("colAmount")}</TableCell>
              <TableCell>{t("colMethod")}</TableCell>
              <TableCell>{t("colStatus")}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.orderId}>
                <TableCell>
                  <div>{row.companyName || `#${row.companyId}`}</div>
                  <div className={classes.sub}>{row.email || row.whatsapp}</div>
                </TableCell>
                <TableCell>
                  <div>{row.planId}</div>
                  <div className={classes.sub}>{row.orderId} · {formatDateTime(row.updatedAt)}</div>
                </TableCell>
                <TableCell>{row.moneda} {row.monto}</TableCell>
                <TableCell>{row.paymentMethodName || "—"}</TableCell>
                <TableCell><PaymentStatusChip row={row} /></TableCell>
                <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                  {row.paymentStatus === "review" ? (
                    <>
                      <Button size="small" disabled={busy || !row.hasReceipt} onClick={() => showReceipt(row)}>{t("view")}</Button>
                      <Button size="small" color="primary" disabled={busy} onClick={() => approve(row)}>{t("approve")}</Button>
                      <Button size="small" disabled={busy} onClick={() => { setRejecting(row); setReason(""); }}>{t("reject")}</Button>
                    </>
                  ) : row.paymentStatus === "paid" && row.partnerError ? (
                    <Button size="small" color="primary" disabled={busy} onClick={() => approve(row)}>{t("retry")}</Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow><TableCell colSpan={6}>{t("empty")}</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!receipt} onClose={() => setReceipt(null)} fullWidth maxWidth="sm" PaperProps={{ className: dlg.paper }}>
        <DialogTitle>{t("receiptTitle").replace("{company}", receipt?.row?.companyName || "")}</DialogTitle>
        <DialogContent>
          {receipt ? (
            <>
              <div className={classes.sub} style={{ marginBottom: 8 }}>
                {receipt.row.paymentMethodName} · {receipt.row.moneda} {receipt.row.monto} · {receipt.row.planId}
              </div>
              <img className={classes.receipt} src={receipt.url} alt={t("receiptTitle")} />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceipt(null)}>{t("close")}</Button>
          <Button disabled={busy} onClick={() => { setRejecting(receipt.row); setReason(""); }}>{t("reject")}</Button>
          <Button color="primary" variant="contained" disabled={busy} onClick={() => approve(receipt.row)}>{t("approve")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejecting} onClose={() => setRejecting(null)} fullWidth maxWidth="xs" PaperProps={{ className: dlg.paper }}>
        <DialogTitle>{t("rejectTitle")}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={2} variant="outlined" label={t("reason")} value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>{t("close")}</Button>
          <Button color="primary" variant="contained" disabled={busy || !reason.trim()} onClick={reject}>{t("reject")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
